import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { Link } from "react-router";

import type { inferRouterOutputs } from "@trpc/server";

import type { AppRouter } from "@runcheetah/api/router";
import {
  CodeText,
  DataTable,
  MoneyValue,
  PageHeader,
  QueryErrorState,
  SortableTableHeader,
  TableSkeleton,
} from "@/components/common";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type User = RouterOutputs["admin"]["users"]["list"][number];

const formatDate = formatDateTime;

const columns: ColumnDef<User>[] = [
  {
    accessorKey: "email",
    header: ({ column }) => <SortableTableHeader column={column}>Email</SortableTableHeader>,
    cell: ({ row }) => (
      <Link to={`/users/${row.original.id}`} className="font-medium hover:underline">
        {row.original.email}
      </Link>
    ),
  },
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">{row.original.name || "—"}</span>
    ),
  },
  {
    accessorKey: "totalCostUsd",
    header: ({ column }) => <SortableTableHeader column={column}>Total Cost</SortableTableHeader>,
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">
        <MoneyValue amount={row.original.totalCostUsd} />
      </span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => <SortableTableHeader column={column}>Joined</SortableTableHeader>,
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">{formatDate(row.original.createdAt)}</span>
    ),
  },
  {
    accessorKey: "updatedAt",
    header: "Last Updated",
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">{formatDate(row.original.updatedAt)}</span>
    ),
  },
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => <CodeText>{row.original.id}</CodeText>,
  },
];

export function UsersTable({ data }: { data: User[] }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <DataTable table={table} columnsCount={columns.length} emptyMessage="No users found." />;
}

export function UsersPage() {
  const { data, isPending, error } = trpc.admin.users.list.useQuery({});

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Users" meta="Loading..." />
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    const isForbidden = error.data?.code === "FORBIDDEN";
    return (
      <QueryErrorState
        title="Failed to load users"
        message={error.message}
        isForbidden={isForbidden}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Users" meta={`${data.length} ${data.length === 1 ? "user" : "users"}`} />
      <UsersTable data={data} />
    </div>
  );
}
